import React from 'react';
import { DateTime } from 'luxon';
import { Tabs } from '@fikasio/react-ui-components';
import { useTranslation } from 'react-i18next';
import PaneHeader from 'components/UI/PaneHeader';
import UserAvatar from 'components/UI/UserAvatar';
import { useGetUsersQuery } from 'services/users/api';
import { toDateTime } from 'utils/date';
import UserPermissions from './UserPermissions';
import styles from './UserPane.module.css';

const languageName = (language?: string) => {
  if (language === 'en') {
    return 'English';
  }
  if (language === 'fr') {
    return 'Français';
  }
  return language;
};

const UserPane = ({
  id,
}: {
  id: string;
}) => {
  const { i18n, t } = useTranslation();
  const { data: users } = useGetUsersQuery();
  const user = (users || []).find(item => item.id === id);

  const formatWhen = (value?: Date | string) => {
    const dateTime = toDateTime(value);
    if (!dateTime?.isValid) {
      return '—';
    }
    return dateTime.setLocale(i18n.language).toLocaleString(DateTime.DATETIME_MED);
  };

  return (
    <>
      <PaneHeader showExpandIcon={false}>
        <h4>{user?.name}</h4>
      </PaneHeader>
      {
        user && (
          <>
            <div className={styles.identity}>
              <UserAvatar
                alt={user.name}
                hasProfilePicture={user.hasProfilePicture}
                modifiedAt={user.modifiedAt}
                size={72}
                userId={user.id}
              />
            </div>
            <Tabs
              className="mb-3"
              defaultValue="INFOS"
              options={[
                {
                  content: (
                    <dl className={styles.details}>
                      <div>
                        <dt>{t('emailAddress')}</dt>
                        <dd>{user.emailAddress}</dd>
                      </div>
                      <div>
                        <dt>{t('language')}</dt>
                        <dd>{languageName(user.language) || '—'}</dd>
                      </div>
                      <div>
                        <dt>{t('created')}</dt>
                        <dd>{formatWhen(user.createdAt)}</dd>
                      </div>
                      <div>
                        <dt>{t('lastUpdate')}</dt>
                        <dd>{formatWhen(user.modifiedAt)}</dd>
                      </div>
                    </dl>
                  ),
                  label: t('informations'),
                  value: 'INFOS',
                },
                {
                  content: (
                    <UserPermissions permissions={user.permissions} />
                  ),
                  label: t('permissions'),
                  value: 'PERMISSIONS',
                },
              ]}
            />
          </>
        )
      }
    </>
  );
};

export default UserPane;
